import { Checkbox, CheckboxProps, FormControlLabel, FormControlLabelProps } from '@mui/material';
import { FieldValues, FieldPath, UseControllerProps, useController } from 'react-hook-form';

export type CheckboxInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> = UseControllerProps<TFieldValues, TName> &
  Omit<FormControlLabelProps, 'control' | 'defaultValue'> & { CheckboxProps?: CheckboxProps };

export const CheckboxInput = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  name,
  control,
  rules,
  defaultValue,
  onChange,
  CheckboxProps,
  ...FormControlLabelProps
}: CheckboxInputProps<TFieldValues, TName>) => {
  const { field } = useController({
    name,
    control,
    rules,
    defaultValue
  });

  return (
    <FormControlLabel
      {...field}
      checked={field.value}
      onChange={(event, checked) => {
        field.onChange(checked);
        onChange?.(event, checked);
      }}
      control={<Checkbox {...CheckboxProps} />}
      {...FormControlLabelProps}
    />
  );
};

export type UnlabeledCheckboxInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> = UseControllerProps<TFieldValues, TName> & CheckboxProps;

export const UnlabeledCheckboxInput = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  name,
  control,
  rules,
  defaultValue,
  onChange,
  disabled,
  shouldUnregister,
  ...CheckboxProps
}: UnlabeledCheckboxInputProps<TFieldValues, TName>) => {
  const { field } = useController({ name, control, rules, defaultValue, disabled, shouldUnregister });

  return (
    <Checkbox
      {...field}
      checked={field.value}
      onChange={(event, checked) => {
        field.onChange(checked);
        onChange?.(event, checked);
      }}
      {...CheckboxProps}
    />
  );
};
